import { PropsWithChildren, useEffect, useState } from "react"
import { useSnackbar } from "notistack"
import { ErrorDialog } from "./ErrorDialog"
import { AppErrorType, ErrorDisplay } from "../../../core/api/errorTypes"
import { useAppDispatch, useAppSelector } from "../../../modules/store/storeHooks"
import { selectCurrentError, selectErrorCount } from "./store/errorSelectors"
import { dequeueError, removeCurrentError } from "./store/errorSlice"
import { UnreachableCaseError } from "../../../lib/lang"

type ErrorAdapterProps = Readonly<PropsWithChildren<{}>>

export function ErrorAdapter({ children }: ErrorAdapterProps) {
    const [dialogError, setDialogError] = useState<AppErrorType>()

    const currentError = useAppSelector(selectCurrentError)
    const errorCount = useAppSelector(selectErrorCount)
    const dispatch = useAppDispatch()

    const { enqueueSnackbar } = useSnackbar()

    function handleErrorDialogClose() {
        setDialogError(undefined)
        dispatch(removeCurrentError())
    }

    useEffect(() => {
        if (currentError) {
            switch (currentError.display) {
                case ErrorDisplay.SNACKBAR:
                    {
                        enqueueSnackbar({
                            variant: "error",
                            message: currentError.error.displayMessage,
                        })

                        dispatch(removeCurrentError())
                    }
                    break
                case ErrorDisplay.DIALOG:
                    {
                        setDialogError(currentError.error)
                        // NOTE: Do NOT remove current error until dialog has been closed
                    }
                    break

                case ErrorDisplay.ROUTE:
                    {
                        dispatch(removeCurrentError())
                    }
                    break
                case ErrorDisplay.NONE:
                    {
                        dispatch(removeCurrentError())
                    }
                    break
                default:
                    throw new UnreachableCaseError(currentError.display)
            }
        } else if (errorCount > 0) {
            dispatch(dequeueError())
        }
    }, [currentError, errorCount])

    return (
        <>
            {children}
            {dialogError && (
                <ErrorDialog error={dialogError} open={Boolean(dialogError)} onClose={handleErrorDialogClose} />
            )}
        </>
    )
}
